/**
 * CRM FCM push.
 * Separate subscribe endpoint → CrmPushSubscription.
 *
 * `syncCrmFcmPush`: used on authenticated entry (silent) and from Profile.
 * - permission `granted` → refresh token + subscribe (no prompt)
 * - permission `default` → requestPermission once, then subscribe
 * - `denied` / unsupported / local opt-out → skip
 *
 * Browser cannot revoke Notification.permission; "Turn off" = opt-out +
 * deleteToken + unsubscribe so silent sync does not re-enroll.
 */
import { deleteToken, getMessaging, getToken, isSupported } from 'firebase/messaging';
import { initializeApp, getApps, type FirebaseApp } from 'firebase/app';
import { environment } from '../../../environments/environment';

type FirebaseEnv = {
  apiKey?: string;
  authDomain?: string;
  projectId?: string;
  appId?: string;
  messagingSenderId?: string;
  storageBucket?: string;
  vapidKey?: string;
};

export type CrmPushSyncResult = 'subscribed' | 'denied' | 'unavailable' | 'opted_out';
export type CrmPushDisableResult = 'disabled' | 'unavailable';

/** Device-local opt-out — JS cannot revoke Notification.permission. */
const CRM_PUSH_OPT_OUT_KEY = 'ewents-crm-push-opt-out';

function firebaseCfg(): FirebaseEnv {
  const e = environment as typeof environment & { firebase?: FirebaseEnv };
  return e.firebase ?? {};
}

export function isCrmPushConfigured(): boolean {
  const cfg = firebaseCfg();
  return Boolean(
    cfg.apiKey?.trim() &&
    cfg.authDomain?.trim() &&
    cfg.projectId?.trim() &&
    cfg.appId?.trim() &&
    cfg.messagingSenderId?.trim() &&
    cfg.vapidKey?.trim()
  );
}

export function isCrmPushOptedOut(): boolean {
  try {
    return localStorage.getItem(CRM_PUSH_OPT_OUT_KEY) === '1';
  } catch {
    return false;
  }
}

function setCrmPushOptedOut(optedOut: boolean): void {
  try {
    if (optedOut) localStorage.setItem(CRM_PUSH_OPT_OUT_KEY, '1');
    else localStorage.removeItem(CRM_PUSH_OPT_OUT_KEY);
  } catch {
    // Ignore quota / private-mode failures — sync still gated on best-effort flag.
  }
}

/** Profile chip / button: OS permission granted and user has not opted out here. */
export function isCrmBrowserPushActive(): boolean {
  return (
    isCrmPushConfigured() &&
    typeof Notification !== 'undefined' &&
    Notification.permission === 'granted' &&
    !isCrmPushOptedOut()
  );
}

function getFirebaseApp(): FirebaseApp | null {
  if (!isCrmPushConfigured()) return null;
  const cfg = firebaseCfg();
  if (getApps().length > 0) return getApps()[0]!;
  return initializeApp({
    apiKey: cfg.apiKey!,
    authDomain: cfg.authDomain!,
    projectId: cfg.projectId!,
    appId: cfg.appId!,
    messagingSenderId: cfg.messagingSenderId!,
    ...(cfg.storageBucket ? { storageBucket: cfg.storageBucket } : {}),
  });
}

function buildSwUrl(): string {
  const cfg = firebaseCfg();
  const params = new URLSearchParams({
    apiKey: cfg.apiKey ?? '',
    authDomain: cfg.authDomain ?? '',
    projectId: cfg.projectId ?? '',
    messagingSenderId: cfg.messagingSenderId ?? '',
    appId: cfg.appId ?? '',
  });
  return `/firebase-messaging-sw.js?${params.toString()}`;
}

async function waitForServiceWorkerRegistration(
  registration: ServiceWorkerRegistration,
  timeoutMs = 15_000
): Promise<ServiceWorkerRegistration> {
  if (registration.active) return registration;
  const installing = registration.installing ?? registration.waiting;
  if (installing) {
    await new Promise<void>((resolve, reject) => {
      const timer = window.setTimeout(() => reject(new Error('sw_timeout')), timeoutMs);
      const onChange = () => {
        if (installing.state === 'activated') {
          window.clearTimeout(timer);
          installing.removeEventListener('statechange', onChange);
          resolve();
        }
        if (installing.state === 'redundant') {
          window.clearTimeout(timer);
          installing.removeEventListener('statechange', onChange);
          reject(new Error('sw_redundant'));
        }
      };
      installing.addEventListener('statechange', onChange);
    });
    return registration;
  }
  await navigator.serviceWorker.ready;
  return registration;
}

async function resolveMessagingRegistration(): Promise<{
  messaging: ReturnType<typeof getMessaging>;
  registration: ServiceWorkerRegistration;
  vapidKey: string;
} | null> {
  const app = getFirebaseApp();
  const vapidKey = firebaseCfg().vapidKey;
  if (!app || !vapidKey || !('serviceWorker' in navigator)) return null;

  const registration = await navigator.serviceWorker.register(buildSwUrl(), {
    scope: '/firebase-cloud-messaging-push-scope',
  });
  await waitForServiceWorkerRegistration(registration);
  return { messaging: getMessaging(app), registration, vapidKey };
}

/**
 * Register / refresh FCM device token. Safe to call on every authenticated visit.
 * No UI — callers decide toasts (Profile) or silence (layout).
 */
export async function syncCrmFcmPush(
  subscribe: (token: string) => Promise<unknown>
): Promise<CrmPushSyncResult> {
  if (!isCrmPushConfigured() || !('Notification' in window) || !('serviceWorker' in navigator)) {
    return 'unavailable';
  }
  if (isCrmPushOptedOut()) return 'opted_out';

  const supported = await isSupported().catch(() => false);
  if (!supported) return 'unavailable';

  let permission = Notification.permission;
  if (permission === 'default') {
    permission = await Notification.requestPermission();
  }
  if (permission !== 'granted') return 'denied';

  const ctx = await resolveMessagingRegistration();
  if (!ctx) return 'unavailable';

  const token = await getToken(ctx.messaging, {
    vapidKey: ctx.vapidKey,
    serviceWorkerRegistration: ctx.registration,
  });
  if (!token) return 'unavailable';

  await subscribe(token);
  return 'subscribed';
}

/** Profile Allow — clears local opt-out then syncs. */
export async function enableCrmFcmPush(
  subscribe: (token: string) => Promise<unknown>
): Promise<CrmPushSyncResult> {
  setCrmPushOptedOut(false);
  return syncCrmFcmPush(subscribe);
}

/**
 * Profile Turn off — opt-out locally, drop FCM token, unsubscribe backend.
 * Silent layout sync respects opt-out so it does not re-enroll.
 */
export async function disableCrmFcmPush(
  unsubscribe: (token: string) => Promise<unknown>
): Promise<CrmPushDisableResult> {
  setCrmPushOptedOut(true);
  if (
    !isCrmPushConfigured() ||
    !('Notification' in window) ||
    Notification.permission !== 'granted'
  ) {
    return 'disabled';
  }
  const supported = await isSupported().catch(() => false);
  if (!supported) return 'disabled';

  try {
    const ctx = await resolveMessagingRegistration();
    if (!ctx) return 'disabled';
    const token = await getToken(ctx.messaging, {
      vapidKey: ctx.vapidKey,
      serviceWorkerRegistration: ctx.registration,
    });
    if (token) {
      await unsubscribe(token).catch(() => undefined);
      await deleteToken(ctx.messaging).catch(() => undefined);
    }
    return 'disabled';
  } catch {
    return 'unavailable';
  }
}
